import type { ReactNode } from 'react';
import { HelpTrigger } from '../HelpTrigger';
import './Field.css';

export interface FieldProps {
  /** Optional label text. Omit for a bare control. */
  label?: ReactNode;
  /** `id` of the control this label describes — wires label → control for a11y. */
  htmlFor?: string;
  /**
   * Optional help content. When provided, a `<HelpTrigger>` is rendered in the
   * label row showing this content in a floating caption.
   */
  help?: ReactNode;
  /**
   * Accessible label for the help trigger button. Only used when `help` is
   * set. Defaults to "Help".
   */
  helpLabel?: string;
  /** The control itself — an input, select, etc. */
  children: ReactNode;
  /** Optional class on the wrapping element. */
  className?: string;
}

/**
 * A vertically-stacked form field: an optional label row (with an optional
 * help trigger) above a control.
 */
export function Field({ label, htmlFor, help, helpLabel, children, className }: FieldProps) {
  const hasHeader = label != null || help != null;

  return (
    <div className={['alc-field', className].filter(Boolean).join(' ')}>
      {hasHeader && (
        <div className="alc-field-header">
          {label != null ? (
            <label className="alc-field-label" htmlFor={htmlFor}>
              {label}
            </label>
          ) : (
            <span />
          )}
          {help != null && <HelpTrigger caption={help} label={helpLabel} />}
        </div>
      )}
      {children}
    </div>
  );
}
